import type { OnInit } from '@angular/core';
import { Component, computed, inject, input } from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxCustomAddress, OnyxTime } from '@onyx/angular';
import {
  OnyxAddressInputComponent,
  OnyxDatepickerComponent,
  OnyxDatePipe,
  OnyxFormGroupComponent,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTimepickerComponent,
} from '@onyx/angular';
import { startWith } from 'rxjs';
import { SuggestionHelper } from '../../../../common/helpers/suggestions.helper';
import { DelegationsFormService } from '../../common/services/delegations-form.service';

type DelegationStartFormGroup = ReturnType<
  (typeof DelegationStartFormComponent)['buildForm']
>;

@Component({
  selector: 'app-delegation-start-form',
  imports: [
    OnyxSectionComponent,
    OnyxTimepickerComponent,
    ReactiveFormsModule,
    OnyxAddressInputComponent,
    OnyxDatepickerComponent,
    OnyxSuggestionsComponent,
    OnyxFormGroupComponent,
  ],
  templateUrl: './delegation-start-form.component.html',
  styleUrl: './delegation-start-form.component.scss',
})
export class DelegationStartFormComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly delegationsFormService = inject(DelegationsFormService);

  public readonly form = input.required<DelegationStartFormGroup>();
  public readonly hasDailyReturns = input(false);

  protected readonly I18N = 'delegations.delegationPeriodForm';
  protected readonly DATE_SUGGESTIONS = SuggestionHelper.getDateSuggestions(
    this.translateService,
    this.datePipe,
  );

  protected driverBase = this.delegationsFormService.driverBase;
  protected driverHome = this.delegationsFormService.driverHome;
  protected vehicleBase = this.delegationsFormService.vehicleBase;

  protected addressSuggestions = computed(() =>
    SuggestionHelper.getDelegationAddressSuggestions(
      this.driverBase(),
      this.driverHome(),
      this.vehicleBase(),
    ),
  );

  public ngOnInit(): void {
    const controls = this.form().controls;
    controls.departureDate.valueChanges
      .pipe(startWith(controls.departureDate.getRawValue()))
      .subscribe((startDate) =>
        this.delegationsFormService.startDate.set(startDate),
      );
    controls.departureTime.valueChanges
      .pipe(startWith(controls.departureTime.getRawValue()))
      .subscribe((startTime) =>
        this.delegationsFormService.startTime.set(startTime ?? null),
      );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      departurePlace: fb.control<OnyxCustomAddress | null>(null, [
        Validators.required,
      ]),
      departureDate: fb.control<string | null>(null, [Validators.required]),
      departureTime: fb.control<OnyxTime | null>(null, [Validators.required]),
    });
  }
}
